import React, { useCallback } from "react";
import styled, { css } from "styled-components";
import { breakpoints, BreakpointSizesType, ColProps, RowProps } from "./types";

const DEFAULT_GUTTER = 16;
const DEFAULT_COLUMNS = 12;

export const ColAuto = css`
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
`;

export const ColSize = (size: number) => {
  if (Number.isNaN(size)) return "";
  return css`
    flex: 0 0 ${Number.isNaN(size) ? 100 : size}%;
    max-width: ${Number.isNaN(size) ? 100 : size}%;
  `;
};

export const ColOffset = (size: number) => css`
  margin-left: ${size}%;
`;

export const renderCols = (props: ColProps) =>
  Object.entries(breakpoints).map(
    ([modifier, breakpoint]: [BreakpointSizesType, number]) => {
      const width = 100 * ((props[modifier] as number) / (props.cols || 1));
      const widthOffset =
        100 * ((props[`${modifier}Offset`] as number) / (props.cols || 1));
      return css`
        @media screen and (min-width: ${breakpoint}px) {
          ${props[modifier] === "auto" ? ColAuto : ColSize(width)}
          ${Number.isNaN(widthOffset) ? "" : ColOffset(widthOffset)}
        }
      `;
    }
  );

export const Col = styled.div<ColProps>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  ${(props) => renderCols(props)};
`;

export const TableCol = styled.td<ColProps>`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  ${(props) => renderCols(props)};
`;

export const RowStyles = styled.div<{ gutter?: number; maxWidth?: number }>`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;

  ${({ gutter, maxWidth }) => css`
    width: calc(100% + ${gutter || DEFAULT_GUTTER}px);
    max-width: ${maxWidth ? `${maxWidth}px` : "100%"};
    margin-left: -${gutter || DEFAULT_GUTTER}px;

    ${Col} {
      padding-left: ${gutter || DEFAULT_GUTTER}px;
    }
  `}
`;

export const TableRowStyles = styled.tr<{ gutter?: number; maxWidth?: number }>`
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  margin: 0 auto;

  ${({ gutter, maxWidth }) => css`
    width: calc(100% + ${gutter || DEFAULT_GUTTER}px);
    max-width: ${maxWidth ? `${maxWidth}px` : "100%"};
    margin-left: -${gutter || DEFAULT_GUTTER}px;

    ${TableCol} {
      padding-left: ${gutter || DEFAULT_GUTTER}px;
    }
  `}
`;

export const Row = ({
  cols = DEFAULT_COLUMNS,
  gutter,
  className,
  maxWidth,
  children,
}: RowProps) => {
  const renderChildren = useCallback(
    () =>
      React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement<ColProps>(child, {
            cols,
          });
        }
        return child;
      }),
    [children, cols]
  );

  return (
    <RowStyles gutter={gutter} className={className} maxWidth={maxWidth}>
      {renderChildren()}
    </RowStyles>
  );
};

export const TableRow = ({
  cols = DEFAULT_COLUMNS,
  gutter,
  className,
  maxWidth,
  children,
}: RowProps) => {
  const renderChildren = useCallback(
    () =>
      React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement<ColProps>(child, {
            cols,
          });
        }
        return child;
      }),
    [children, cols]
  );

  return (
    <TableRowStyles gutter={gutter} className={className} maxWidth={maxWidth}>
      {renderChildren()}
    </TableRowStyles>
  );
};
